import { CalloutComponent, CheckBox, FormValues, getValue, InputFormCallout, InputFormGroup } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { IntegerInput } from "../../components/IntegerInput/IntegerInput";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  ADBS_UPDATE_POLL_DELAY,
  ADBS_UPDATE_POLL_INTERVAL,
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export interface AdbsScaleValues {
  isAutoScalingEnabled: boolean | undefined,
  cpuCoreCount: number | undefined,
  dataStorageSizeInTBs: number | undefined,
}

export interface AdbsScaleProps extends OperationActionProps {
  databaseId: string | undefined;
  adbsScaleValues: AdbsScaleValues;
}

const enum Fields {
  AutoScaling = "autoScaling",
  CoreCount = "coreCount",
  StorageSize = "storageSize",
}

const AdbsScale = (
  { targetId, location, databaseId, adbsScaleValues, onExecute, onCancel }: AdbsScaleProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resGroup = decodeURIComponent(resourceGroup || "");
  const resName = decodeURIComponent(resourceName || "");
  const [ocpuCalloutRef, setOcpuCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);

  const { invokeCall: invokeAdbsUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).adbsDatabaseApi.updateAdbsDatabase },
  );

  const onScaleAdbs = (formValues: FormValues): void => {
    const cpuCoreCount = getValue(formValues, Fields.CoreCount, InputFormGroup);
    const dataStorageSizeInTBs = getValue(formValues, Fields.StorageSize, InputFormGroup);
    const isAutoScalingEnabled = getValue(formValues, Fields.AutoScaling, InputFormGroup);

    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: () => {
        ocpuCalloutRef.allowResubmit();
      },
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.scaleAdbs(),
          message: Messages.notifications.inProgress.messages.scaleAdbs(resName),
        },
        success: {
          title: Messages.notifications.success.titles.scaleAdbs(),
          message: Messages.notifications.success.messages.scaleAdbs(resName),
        },
        failure: {
          title: Messages.notifications.failure.titles.scale(),
          message: Messages.notifications.failure.messages.scaleAdbs(resName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.ADBS_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            databaseName: resName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.ADBS_UPDATED_CHECK,
          delay: ADBS_UPDATE_POLL_DELAY,
          interval: ADBS_UPDATE_POLL_INTERVAL,
        },
      },
    };

    invokeAdbsUpdate({
      databaseName: resName,
      resourceGroupName: resGroup,
      subscriptionId,
      updateAdbsDatabaseDetails: { cpuCoreCount, dataStorageSizeInTBs, isAutoScalingEnabled },
    }, invokeOptions);
  };

  return (
    <InputFormCallout
      onSubmit={onScaleAdbs}
      componentRef={setOcpuCalloutRef}
      targetId={targetId}
      onClose={onCancel}
      primaryButtonText={Messages.actions.scale()}
      title={Messages.createNewPanels.scaleCoreCount.title()}
    >
      <CheckBox
        fieldName={Fields.AutoScaling}
        defaultValue={adbsScaleValues.isAutoScalingEnabled}
        label={Messages.labels.ocpuAutoScaling()}
        tooltip={Messages.hints.ocpuAutoScaling()}
      />
      <IntegerInput
        min={1}
        max={128}
        fieldName={Fields.CoreCount}
        defaultValue={adbsScaleValues.cpuCoreCount}
        required
        label={Messages.labels.ocpuCount()}
      />
      <IntegerInput
        min={1}
        max={128}
        defaultValue={adbsScaleValues.dataStorageSizeInTBs}
        required
        label={Messages.labels.storageInTB()}
        fieldName={Fields.StorageSize}
      />
    </InputFormCallout>
  );
};

export const newAdbsScale = (
  props: AdbsScaleProps,
): JSX.Element => (<AdbsScale {...props} />);
